import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  IUrlPresignedResponse,
  IUrlPresignedResponseList,
  UserConfigsWMSListModel,
  UserConfigsWMSModel,
  UserRolesListModel,
  UserWMSFilterModel,
  UserWMSListModel,
  UserWMSModel,
} from '@kanzi-apes/kanzi-models';
import { KanziRestClientService } from '@kanzi-apes/kanzi-rest-api';
import { EnvironmentService, sanitizeFilters } from '@kanzi-apes/kanzi-utils';
import {
  ICreateUserWms,
  IUpdateUserConfigWms,
} from '../../models/create-user.interface';

@Injectable({
  providedIn: 'root',
})
export class UsersWMSService {
  private httpClient = inject(KanziRestClientService);
  private envService = inject(EnvironmentService);

  private usersWMSUrl = `${this.envService.wmsEnvironment?.apiRestModules.kongWMSAPI?.users?.apiURL}`;
  private usersWMSPath = `${this.envService.wmsEnvironment?.apiRestModules.kongWMSAPI?.users?.apiPath}`;
  private usersRolesWMSUrl = `${this.envService.wmsEnvironment?.apiRestModules.kongWMSAPI?.user_roles?.apiURL}`;
  private usersRolesWMSPath = `${this.envService.wmsEnvironment?.apiRestModules.kongWMSAPI?.user_roles?.apiPath}`;
  private usersCreateWMSUrl = `${this.envService.wmsEnvironment?.apiRestModules.kongWMSAPI?.user_creation?.apiURL}`;
  private usersCreateWMSPath = `${this.envService.wmsEnvironment?.apiRestModules.kongWMSAPI?.user_creation?.apiPath}`;
  private usersUpdateWMSUrl = `${this.envService.wmsEnvironment?.apiRestModules.kongWMSAPI?.userConfigUpdate?.apiURL}`;
  private usersUpdateWMSPath = `${this.envService.wmsEnvironment?.apiRestModules.kongWMSAPI?.userConfigUpdate?.apiPath}`;
  private userConfigWMSUrl = `${this.envService.wmsEnvironment?.apiRestModules.kongWMSAPI?.userConfigs?.apiURL}`;
  private userConfigWMSPath = `${this.envService.wmsEnvironment?.apiRestModules.kongWMSAPI?.userConfigs?.apiPath}`;

  /**
   * Method to get the list of users.
   * @param filters {UserWMSFilterModel | null} Filters to apply to the list.
   * @returns Observable {UserWMSListModel}
   */
  getUserList(
    filters: UserWMSFilterModel | null
  ): Observable<UserWMSListModel> {
    const url = `${this.usersWMSUrl}${this.usersWMSPath}`;
    const sanitizedFilters = sanitizeFilters(filters as any);
    return this.httpClient.get(
      url,
      sanitizedFilters,
      'Error al listar los usuarios',
      {},
      { tokenName: 'KanziWMSUserToken', tokenPrefix: 'COGNITO' }
    );
  }
  
  /**
   * Retrieves a list of user roles.
   *
   * @return {Observable<UserRolesListModel>} An observable that emits the list of user roles.
   */
  getUserRolesList(): Observable<UserRolesListModel> {
    const url = `${this.usersRolesWMSUrl}${this.usersRolesWMSPath}`;
    return this.httpClient.get(
      url,
      {},
      'Error al listar los roles',
      {},
      { tokenName: 'KanziWMSUserToken', tokenPrefix: 'COGNITO' }
    );
  }

  getUserConfig(
    filters: UserWMSFilterModel | null
  ): Observable<UserConfigsWMSListModel> {
    const url = `${this.userConfigWMSUrl}${this.userConfigWMSPath}`;
    const sanitizedFilters = sanitizeFilters(filters as any);
    return this.httpClient.get(
      url,
      sanitizedFilters,
      'Error al obtener la configuracion del usuario',
      {},
      { tokenName: 'KanziWMSUserToken', tokenPrefix: 'COGNITO' }
    );
  }
  
  public createUser(body: ICreateUserWms): Observable<UserWMSModel> {
    const url = `${this.usersCreateWMSUrl}${this.usersCreateWMSPath}/admin`;
    return this.httpClient.post(
      url,
      body,
      'Error al crear el usuario.',
      {},
      { tokenName: 'KanziWMSUserToken', tokenPrefix: 'COGNITO' }
    );
  }

  /**
   * Function to update the user configuration.
   * @param body {IUpdateUserConfigWms} - The body of the request.
   * @returns {Observable<UserConfigsWMSModel>} - The observable with the response.
   */
  updateUserConfigs(
    body: IUpdateUserConfigWms
  ): Observable<UserConfigsWMSModel> {
    const url = `${this.usersUpdateWMSUrl}${this.usersUpdateWMSPath}/${body.id}`;
    return this.httpClient.patch(
      url,
      body,
      {},
      'Error al crear el usuario.',
      {},
      { tokenName: 'KanziWMSUserToken', tokenPrefix: 'COGNITO' }
    );
  }

  public getUrlPresignedToUploadPicture(
    filters: UserWMSFilterModel | null
  ): Observable<IUrlPresignedResponseList> {
    const url = `${this.usersUpdateWMSUrl}user_configs/put_presigned_url`;
    const sanitizedFilters = sanitizeFilters(filters as any);
    return this.httpClient.get(
      url,
      sanitizedFilters,
      'Error al obtener la url prefirmada',
      {},
      { tokenName: 'KanziWMSUserToken', tokenPrefix: 'COGNITO' }
    );
  }
}